package com.vizio.redwolf.auth.api

import com.vizio.redwolf.auth.model.AccountAbuseFormInfo
import com.vizio.redwolf.auth.model.AccountAuthCodeRequestInfo
import com.vizio.redwolf.auth.model.AccountAuthCodeResponse
import com.vizio.redwolf.auth.model.AccountEmailExistsRequestBody
import com.vizio.redwolf.auth.model.AccountEmailExistsResponse
import com.vizio.redwolf.auth.model.AccountForgotPasswordInfo
import com.vizio.redwolf.auth.model.AccountRegisterProductInfo
import com.vizio.redwolf.auth.model.AccountRegisteredProductsResponse
import com.vizio.redwolf.auth.model.WalmartAccountRelinkResponse
import com.vizio.redwolf.auth.model.AccountRequestJwtResponse
import com.vizio.redwolf.auth.model.AccountResult
import com.vizio.redwolf.auth.model.AccountSerialNumberInfo
import com.vizio.redwolf.auth.model.AccountSerialNumberResponse
import com.vizio.redwolf.auth.model.AccountShippingAddress
import com.vizio.redwolf.auth.model.AccountUnsubscribeInfo
import com.vizio.redwolf.auth.model.AccountUser
import com.vizio.redwolf.auth.model.AccountUserEmailPreference
import com.vizio.redwolf.auth.model.SwapOneOrAllDevicesRequestInfo
import com.vizio.redwolf.auth.model.CreateAccount2RequestInfo
import com.vizio.redwolf.auth.model.CreateAccountRequestInfo
import com.vizio.redwolf.auth.model.CreateAccountResponse
import com.vizio.redwolf.auth.model.EmailVerifiedRequestInfo
import com.vizio.redwolf.auth.model.EmailVerifiedResponse
import com.vizio.redwolf.auth.model.GenericAccountResponse
import com.vizio.redwolf.auth.model.LoginRequestInfo
import com.vizio.redwolf.auth.model.LoginResponse
import com.vizio.redwolf.auth.model.MigrateGuestAccountResponse
import com.vizio.redwolf.auth.model.MigrateGuestRequestInfo
import com.vizio.redwolf.auth.model.RefreshJwtRequestInfo
import com.vizio.redwolf.auth.model.RefreshTokenResponse
import com.vizio.redwolf.auth.model.RequiresVerificationRequestInfo
import com.vizio.redwolf.auth.model.RequiresVerificationResponse
import com.vizio.redwolf.auth.model.SendTermsEmailResponse
import com.vizio.redwolf.auth.model.SendTermsRequestInfo
import com.vizio.redwolf.auth.model.SendVerificationEmailRequestInfo
import com.vizio.redwolf.auth.model.SendVerificationEmailResponse
import com.vizio.redwolf.auth.model.SocialLoginRequestInfo
import com.vizio.redwolf.auth.model.UpdateEmailRequestInfo
import com.vizio.redwolf.auth.model.UpdateEmailV2RequestInfo
import com.vizio.redwolf.auth.model.UpdatePasswordInfo
import com.vizio.redwolf.auth.model.UpdateUserDetailsRequestInfo
import com.vizio.redwolf.auth.model.UserDetailsRequestInfo
import com.vizio.redwolf.auth.model.ConvertWalmartAccountRequestInfo
import com.vizio.redwolf.auth.model.ConvertToWalmartAccountWithSetupCodeRequestInfo
import com.vizio.redwolf.auth.model.ConvertWalmartAccountWithTokenRequestInfo
import com.vizio.redwolf.auth.model.CreateWalmartAccountRequestInfo
import com.vizio.redwolf.auth.model.DeleteVizioAccountRequestInfo
import com.vizio.redwolf.auth.model.DeleteVizioAccountResponse
import com.vizio.redwolf.auth.model.WalmartLoginRequestInfo
import com.vizio.redwolf.auth.model.EmailOTPRequestInfo
import com.vizio.redwolf.auth.model.EmailOTPResponse
import com.vizio.redwolf.auth.model.VerifyEmailOTPRequestInfo
import com.vizio.redwolf.auth.model.WalmartAccountDeviceRelinkRequestInfo
import com.vizio.redwolf.auth.model.VerifyEmailOTPWithSetupCodeRequestInfo
import com.vizio.redwolf.auth.model.EmailOTPWithSetupCodeRequestInfo
import com.vizio.redwolf.auth.model.SwapOneOrAllDevicesResponse
import com.vizio.redwolf.auth.model.VerifyEmailOTPResponse
import com.vizio.redwolf.auth.model.WalmartLoginViaSetupCodeRequestInfo
import com.vizio.redwolf.auth.model.WalmartRequestOTPWithSetupCodeResponse
import com.vizio.redwolf.auth.model.WalmartVerifyOTPWithSetupCodeResponse
import com.vizio.redwolf.auth.model.WebEmailVerificationRequestInfo
import com.vizio.redwolf.auth.model.WebVerifyTokenResponse
import com.vizio.redwolf.auth.utils.JwtValidationResult

interface AccountApiInterface {

    /**
     * Create Account API to register/signup a new user on app with only email & password
     *
     * @param requestInfo The email & password required to register a new user
     * @return [AccountResult] Representing the outcome of new account creation api call
     */
    suspend fun createAccount(requestInfo: CreateAccountRequestInfo, traceId: String? = null): AccountResult<CreateAccountResponse>

    /**
     * Create account api to perform create account function with additional account details
     * Use this api if first name, last name, postal code, & phone number is to be set along with account creation
     * This is only required for Web Light UI; It is not required for mobile use case
     *
     * @param requestInfo Email, password, first name, last name, postal code, phone number, and tokens to create an account
     * @return [AccountResult] Representing the outcome of new account creation api call
     */
    suspend fun createAccount2(requestInfo: CreateAccount2RequestInfo, traceId: String? = null): AccountResult<CreateAccountResponse>

    /**
     * Login api to perform email & password login function for an account
     *
     * @param requestInfo The email & password required to perform login
     * @return [AccountResult] Representing the outcome of login api call
     */
    suspend fun login(requestInfo: LoginRequestInfo, traceId: String? = null): AccountResult<LoginResponse>

    /**
     * Login api to perform social login function for an account
     * Does not support email password login and the other login api should be used for this functionality
     *
     * @param requestInfo The social tokens required to perform login
     * @return [AccountResult] Representing the outcome of login api call
     */
    suspend fun socialLogin(requestInfo: SocialLoginRequestInfo, traceId: String? = null): AccountResult<LoginResponse>

    /**
     * User details API to get information about specific user
     *
     * @param requestInfo The input parameters required to fetch user details
     * @return [AccountResult] Representing the outcome of user details api
     */
    suspend fun requestUserDetails(requestInfo: UserDetailsRequestInfo, traceId: String? = null): AccountResult<AccountUser>

    /**
     * Update user details API to update information about a specific user
     *
     * @param requestInfo The input parameters required to update user details
     * @return [AccountResult] Representing the outcome of updating user details api
     */
    suspend fun updateUserDetails(requestInfo: UpdateUserDetailsRequestInfo, traceId: String? = null): AccountResult<AccountUser>

    /**
     * Email verified/unverified API call to check verification status
     *
     * @param requestInfo The input parameters required to check email is verified or not
     * @return [AccountResult] Representing the outcome of email verification status api
     */
    suspend fun checkIfEmailVerified(requestInfo: EmailVerifiedRequestInfo, traceId: String? = null): AccountResult<EmailVerifiedResponse>

    /**
     * API to check Email verification required or not for the specific user
     *
     * @param requestInfo The input parameters required to check email verification needed or not
     * @return [AccountResult] Representing the outcome of email verification status api
     */
    suspend fun checkIfRequiresEmailVerification(requestInfo: RequiresVerificationRequestInfo, traceId: String? = null): AccountResult<RequiresVerificationResponse>

    /**
     * API to send email verification mail to verify email account
     *
     * @param requestInfo The input parameters required to send verification email
     * @return [AccountResult] Representing the outcome after sending verification email
     */
    suspend fun sendVerificationEmail(requestInfo: SendVerificationEmailRequestInfo, traceId: String? = null): AccountResult<SendVerificationEmailResponse>

    /**
     * API to send terms & conditions email to specific user
     *
     * @param requestInfo The input parameters required to send terms & condition email
     * @return [AccountResult] Representing the outcome after sending terms & conditions email
     */
    suspend fun sendTermsEmail(requestInfo: SendTermsRequestInfo, traceId: String? = null): AccountResult<SendTermsEmailResponse>

    /**
     * API to migrate a guest account into a Vizio account
     *
     * @param requestInfo The input parameters required to migrate a guest user to vizio user
     * @return [AccountResult] Representing the outcome after migration of a guest account into vizio account
     */
    @Deprecated("Use AccountApi migrateGuestAccountV2", replaceWith = ReplaceWith(expression = "migrateGuestAccountV2()"))
    suspend fun migrateGuestAccount(requestInfo: MigrateGuestRequestInfo, traceId: String? = null): AccountResult<MigrateGuestAccountResponse>

    /**
     * V2 API to migrate a guest account into a Vizio account
     * Requires the same request as the v1 api endpoint but returns back a different response type
     * On Success, user details are returned in the response.
     *
     * @param requestInfo The input parameters required to migrate a guest user to vizio user
     * @return [AccountResult] Representing the outcome after migration of a guest account into vizio account
     */
    suspend fun migrateGuestAccountV2(requestInfo: MigrateGuestRequestInfo, traceId: String? = null): AccountResult<AccountUser>

    /**
     * Verify token api which is only for web so should not be used by Mobile App
     *
     * @param requestInfo The input parameters required to perform token verification
     * @return [AccountResult] Representing the outcome after verifying token
     */
    suspend fun webVerifyToken(requestInfo: WebEmailVerificationRequestInfo, traceId: String? = null): AccountResult<WebVerifyTokenResponse>


    @Deprecated("Use AccountApi updateEmailV2", replaceWith = ReplaceWith("updateEmailV2()", "com.vizio.redwolf.auth.api.AccountApi"))
    suspend fun updateEmail(requestInfo: UpdateEmailRequestInfo, traceId: String? = null): AccountResult<AccountUser>


    /**
     * V2 API to update email for an existing Vizio account.
     * This is for Vizio web usage only and should not be used by mobile apps.
     *
     * @param requestInfo Input parameters containing the new email.
     * @return [AccountResult] Representing the outcome after updating account email in Vizio system
     */
    suspend fun updateEmailV2(requestInfo: UpdateEmailV2RequestInfo, traceId: String? = null): AccountResult<AccountUser>


    /**

     * API to validate & refresh JWT Token if needed.
     *
     * @param requestInfo [RefreshJwtRequestInfo] which need to contain value current Ping JWT, current Ping Access Token & current Ping Refresh Token for SDK to use.
     * @return [JwtValidationResult] Representing the outcome after validating and/or refreshing JWT Token
     */
    suspend fun refreshJWTIfRequired(requestInfo: RefreshJwtRequestInfo, traceId: String? = null): JwtValidationResult

    /**
     * API to refresh access token and refresh token.
     *
     * @param lastRefreshToken The last valid refresh token
     * @return [RefreshTokenResponse] Representing the outcome after refreshing the token
     */
    suspend fun refreshToken(lastRefreshToken: String, traceId: String? = null): AccountResult<RefreshTokenResponse>

    /**
     * API to get the jwt token
     *
     * @param authToken The accessToken for user
     * @return [AccountRequestJwtResponse] Data class containing the jwt string
     */
    suspend fun requestJwt(authToken: String, traceId: String? = null): AccountResult<AccountRequestJwtResponse>

    /**
     * API to check if email exists
     *
     * @param emailExistsRequestBody [AccountEmailExistsRequestBody] Data class containing the email
     * @return [AccountEmailExistsResponse] Data class with boolean member representing email exist or not
     */
    suspend fun emailExists(emailExistsRequestBody: AccountEmailExistsRequestBody, traceId: String? = null): AccountResult<AccountEmailExistsResponse>

    /**
     * API to update a user password
     *
     * @param authToken The accessToken for the user
     * @param pwInfo [UpdatePasswordInfo] Data class containing the old and new password
     * @return [GenericAccountResponse] Returns a generic response
     */
    suspend fun updatePassword(authToken: String, pwInfo: UpdatePasswordInfo, traceId: String? = null): AccountResult<GenericAccountResponse>

    /**
     * API to reset user password
     *
     * @param forgotPasswordInfo [AccountForgotPasswordInfo] User information needed for resetting password
     * @return [GenericAccountResponse] Returns a generic response
     */
    suspend fun resetPasswordRequest(forgotPasswordInfo: AccountForgotPasswordInfo, traceId: String? = null): AccountResult<GenericAccountResponse>

    /**
     * API to logout user
     *
     * @param authToken The user's accessToken
     * @return
     */
    suspend fun logout(authToken: String, traceId: String? = null): AccountResult<Unit>

    suspend fun getShippingAddress(authToken: String, sentryHeader: String? = null, traceId: String? = null): AccountResult<AccountShippingAddress>
    suspend fun updateShippingAddress(authToken: String, newAddress: AccountShippingAddress, sentryHeader: String? = null, traceId: String? = null): AccountResult<AccountShippingAddress>
    suspend fun getRegisteredProducts(authToken: String, sentryHeader: String? = null, traceId: String? = null): AccountResult<AccountRegisteredProductsResponse>
    suspend fun unregisterProduct(authToken: String, serialNumber: String, sentryHeader: String? = null, traceId: String? = null): AccountResult<Unit>
    suspend fun getEmailPreference(authToken: String, sentryHeader: String? = null, traceId: String? = null): AccountResult<AccountUserEmailPreference>
    suspend fun updateEmailPreference(authToken: String, emailPreference: AccountUserEmailPreference, sentryHeader: String? = null, traceId: String? = null): AccountResult<AccountUserEmailPreference>
    suspend fun unsubscribe(unsubscribeInfo: AccountUnsubscribeInfo, sentryHeader: String? = null, traceId: String? = null): AccountResult<Unit>
    suspend fun createAbuseReport(authToken: String, abuseFormInfo: AccountAbuseFormInfo, sentryHeader: String? = null, traceId: String? = null): AccountResult<Unit>
    suspend fun validateSerialNumber(authToken: String, serialNumber: AccountSerialNumberInfo, sentryHeader: String? = null, traceId: String? = null): AccountResult<AccountSerialNumberResponse>
    suspend fun updateRegisteredProducts(authToken: String, serialNumbers: List<String>, sentryHeader: String? = null, traceId: String? = null): AccountResult<AccountRegisteredProductsResponse>
    suspend fun registerProduct(authToken: String, registerProductInfo: AccountRegisterProductInfo, sentryHeader: String? = null, traceId: String? = null): AccountResult<AccountRegisteredProductsResponse>
    suspend fun requestAuthCode(accountAuthCodeRequestInfo: AccountAuthCodeRequestInfo, sentryHeader: String? = null, traceId: String? = null): AccountResult<AccountAuthCodeResponse>

    /**
     * Login to a Walmart account using an authorization code from Walmart/Teflon
     *
     * @param requestInfo The Teflon authorization code, redirect URI, and optional recaptcha token for web
     * @return [LoginResponse] Representing the outcome of the login API request to receive a standard login response on success
     */
    suspend fun walmartLogin(requestInfo: WalmartLoginRequestInfo, traceId: String? = null): AccountResult<LoginResponse>


    /**
     * Login to a Walmart account using an authorization code from Walmart/Teflon
     * @param requestInfo The Vizio authorization code and setup code required to login to a Walmart account
     * @return [LoginResponse] Representing the outcome of the login API request to receive a standard login response on success
     */
    suspend fun walmartLoginWithSetupCode(requestInfo: WalmartLoginViaSetupCodeRequestInfo, traceId: String? = null): AccountResult<LoginResponse>

    /**
     * Create a new Walmart account using a Vizio authorization code
     *
     * @param requestInfo The Vizio authorization code
     * @return [CreateAccountResponse] Representing the outcome of the account creation API request to receive a standard create account response on success
     */
    suspend fun createWalmartAccount(requestInfo: CreateWalmartAccountRequestInfo, traceId: String? = null): AccountResult<CreateAccountResponse>

    /**
     * Converts a Vizio account to a Walmart account using a Vizio authorization code
     *
     * @param requestInfo The Vizio authorization code
     * @return [LoginResponse] Representing the outcome of the account conversion API request to receive a standard login response on success
     */
    suspend fun convertToWalmartAccount(requestInfo: ConvertWalmartAccountRequestInfo, traceId: String? = null): AccountResult<LoginResponse>

    /**
     * Converts a Vizio account to a Walmart account using a Vizio authorization code & an access token
     *
     * @param requestInfo The Vizio authorization code and an access token
     * @return [LoginResponse] Representing the outcome of the account conversion API request to receive a standard login response on success
     */
    suspend fun convertToWalmartAccountWithAccessToken(requestInfo: ConvertWalmartAccountWithTokenRequestInfo, traceId: String? = null): AccountResult<LoginResponse>

    /**
     * Converts a TV account into a Walmart account using setup code and VZ auth code
     *
     * @param requestInfo The TV setup code and VZ auth code
     * @return [LoginResponse] Representing the outcome of the account conversion API request
     */
    suspend fun convertToWalmartAccountWithSetupCode(requestInfo: ConvertToWalmartAccountWithSetupCodeRequestInfo, traceId: String? = null): AccountResult<LoginResponse>

    /**
     * Deletes a given Vizio account using a Vizio authorization code
     *
     * @param requestInfo The Vizio authorization code
     * @return [Unit] Representing the outcome of the account deletion API request to receive a standard Unit/Void type on success
     */
    suspend fun deleteVizioAccount(requestInfo: DeleteVizioAccountRequestInfo, traceId: String? = null): AccountResult<DeleteVizioAccountResponse>

    /**
     * Requests an email-based OTP using a Vizio authorization code
     *
     * @param requestInfo The Vizio authorization code
     * @return [Unit] Representing the outcome of the email OTP API request to receive a standard Unit/Void type on success
     */
    suspend fun requestEmailOTP(requestInfo: EmailOTPRequestInfo, traceId: String? = null): AccountResult<EmailOTPResponse>

    /**
     * Verifies an email-based OTP using a Vizio authorization code
     *
     * @param requestInfo The Vizio authorization code
     * @return [Unit] Representing the outcome of the email OTP API verification request to receive a standard Unit/Void type on success
     */
    suspend fun verifyEmailOTP(requestInfo: VerifyEmailOTPRequestInfo, traceId: String? = null): AccountResult<VerifyEmailOTPResponse>

    /**
     * Requests an email-based OTP and email info using a Vizio authorization code
     * @param requestInfo The Vizio authorization code
     * @return [Unit] Representing the outcome of the email OTP API request to receive a standard Unit/Void type on success
     */
    suspend fun requestEmailOTPWithSetupCode(requestInfo: EmailOTPWithSetupCodeRequestInfo, traceId: String? = null): AccountResult<WalmartRequestOTPWithSetupCodeResponse>

    /**
     * Verifies an OTP for a given setup code
     * @param requestInfo The setup code and password required to verify the OTP
     * @return [AccountResult] Representing the outcome of the OTP verification API request
     */
    suspend fun verifyEmailOTPWithSetupCode(requestInfo: VerifyEmailOTPWithSetupCodeRequestInfo, traceId: String? = null): AccountResult<WalmartVerifyOTPWithSetupCodeResponse>

    /**
     * Relinks a Walmart account to a device using an access token and setup code
     *
     * @param requestInfo The access token and setup code required to relink the Walmart account
     * @return [AccountResult] Representing the outcome of the Walmart account device relink API request
     */
    suspend fun walmartAccountDeviceRelink(requestInfo: WalmartAccountDeviceRelinkRequestInfo, traceId: String? = null): AccountResult<WalmartAccountRelinkResponse>

    /**
     * Swaps one or all devices using a Vizio authorization code
     * @param requestInfo The Vizio authorization code
     * @return [AccountResult] Representing the outcome of the swap one or all devices API request
     */
    suspend fun swapOneOrAllDevices(requestInfo: SwapOneOrAllDevicesRequestInfo, traceId: String? = null): AccountResult<SwapOneOrAllDevicesResponse>

    /**
     * Converts a TV account into a Walmart account and perform device Relink using setup code and VZ auth code
     * @param requestInfo The TV setup code and VZ auth code
     * * @return [WalmartAccountRelinkResponse] Representing the outcome of the account conversion API request
     */
    suspend fun convertToWalmartAccountAndPerformRelink(requestInfo: ConvertToWalmartAccountWithSetupCodeRequestInfo, traceId: String? = null): AccountResult<WalmartAccountRelinkResponse>
}